import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import type { OpenAPI } from "simplydo/interfaces";
import {
  Button,
  Icon,
  Label,
  Loader,
  Message,
  Popup,
  Segment,
} from "simplydo/react/ui";

import util from "utils/utils";

import {
  annualImpactValue,
  generateImpactText,
  getImpactIconName,
  impactStageDefinitions,
  useImpactStageMap,
} from "./statementUtils";
import { useImpactStatements } from "./useImpactStatements";

type StageSegmentProps = {
  ideaId: string;
  stage: OpenAPI.Schemas["ImpactStatement"]["stage"];
  onView?: () => void;
  onCreated?: () => void;
};

export const StageSegment = ({
  ideaId,
  stage,
  onView,
  onCreated,
}: StageSegmentProps) => {
  const { t } = useTranslation();
  const {
    impactStatements,
    statementDefinition,
    isLoading,
    canEditProjectStages,
    creatingStage,
    createImpactStatement,
    deleteImpactStatement,
  } = useImpactStatements(ideaId);

  const impactStatement = impactStatements[stage];
  const segmentMap = useImpactStageMap();
  const def = segmentMap[stage as keyof typeof segmentMap];
  const previousStage = impactStageDefinitions[stage]?.previousStage;
  const previousStatement = previousStage
    ? impactStatements[previousStage]
    : undefined;

  const visibleImpacts = useMemo(() => {
    if (!impactStatement?.impacts) {
      return [];
    }

    const impacts: (typeof impactStatement.impacts)[number][] = [];
    const impactOptions = statementDefinition?.impacts || {};
    for (const optionId in impactOptions) {
      const impactOption = impactOptions[optionId];
      if (impactOption?.enabled) {
        const impact = impactStatement.impacts[optionId];

        if (
          impact?.enabled &&
          impact?.ownerImpact &&
          // @ts-ignore
          (impact?.choice || impact?.value)
        ) {
          impacts.push(impact);
        }
      }
    }

    return impacts;
  }, [impactStatement, statementDefinition]);

  const getLabelStyle = (effect?: string) => {
    if (effect === "cost") {
      return { borderColor: "#f2b8b5", color: "#9f3a38" };
    }
    if (effect === "saving") {
      return { borderColor: "#c2e0c6", color: "#2c662d" };
    }
    return {};
  };

  const handleDelete = () => {
    if (stage === "userEstimate" || !impactStatement?._id) {
      return;
    }

    const confirmMessages = {
      projectEstimate: {
        title: "Delete the project estimate?",
        message:
          "Are you sure you want to delete the project estimate statement? This action is instant and irreversible.",
      },
      finalOutcome: {
        title: "Delete final outcome?",
        message:
          "Are you sure you want to delete the final outcome statement? This action is instant and irreversible.",
      },
    }[stage];

    util
      .confirm(confirmMessages.title, confirmMessages.message)
      .then(() => {
        deleteImpactStatement(stage, impactStatement._id);
      })
      .catch(() => {});
  };

  if (isLoading) {
    return <Loader active inline="centered" />;
  }

  return (
    <Segment secondary={!impactStatement}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div>
          <h3>
            <Icon name={def.icon} /> {def.name}
          </h3>
          {def.description}
        </div>
        {impactStatement ? (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                gap: "8px",
              }}
            >
              {visibleImpacts.map((impact, index) => (
                <Label
                  basic
                  style={getLabelStyle(impact.ownerImpact?.effect)}
                  key={index}
                >
                  <Popup
                    position="right center"
                    content={generateImpactText(true, impact, t)}
                    trigger={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                        }}
                      >
                        <div>
                          <Icon
                            name={getImpactIconName(impact.ownerImpact?.type)}
                          />
                          {impact.ownerImpact?.effect === "cost" ? "-" : ""}
                          {annualImpactValue(true, impact)}
                        </div>
                        {impact.choice?.weight !== undefined ? (
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <Icon
                              name="level up alternate"
                              style={{ marginRight: 2 }}
                            />
                            {impact.choice?.weight || 0}/
                            {impact.ownerImpact?.maxWeight || 0}
                          </div>
                        ) : null}
                      </div>
                    }
                  />
                </Label>
              ))}
            </div>
            <div style={{ display: "flex", flex: 1, marginTop: "1em" }}>
              <Button style={{ flex: 1 }} onClick={onView}>
                <Icon name="eye" />
                View details
              </Button>
              {stage !== "userEstimate" && canEditProjectStages && (
                <>
                  <Button style={{ flex: 1 }} onClick={onView}>
                    <Icon name="pencil" />
                    Edit Details
                  </Button>
                  <Button icon basic onClick={handleDelete}>
                    <Icon name="trash" />
                  </Button>
                </>
              )}
            </div>
          </>
        ) : def.create ? (
          canEditProjectStages ? (
            <Button
              secondary
              onClick={() => {
                createImpactStatement(
                  {
                    impacts: previousStatement?.impacts ?? {},
                    description: previousStatement?.description,
                  },
                  stage,
                  onCreated,
                );
              }}
              loading={creatingStage === stage}
            >
              <Icon name="plus" />
              {def.create}
            </Button>
          ) : (
            <Message>{def.name} has not been created yet.</Message>
          )
        ) : null}
      </div>
    </Segment>
  );
};
